import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Footer } from '@/components/Footer';
import { 
  Home, 
  Wrench, 
  BookOpen, 
  User, 
  History, 
  Shield, 
  Menu,
  LogOut,
  Zap,
  LogIn,
  UserPlus,
  Settings,
  ChevronRight,
  X,
  Sparkles,
  Terminal,
  Crown,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Logo } from '@/components/Logo';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { InteractiveBackground } from '@/components/InteractiveBackground';
import { PlanBadge } from '@/components/PlanBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { motion, AnimatePresence } from 'framer-motion';

interface AppLayoutProps {
  children: ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
  showBackground?: boolean;
}

const navItems = [
  { icon: Home, label: 'Inicio', href: '/' },
  { icon: Wrench, label: 'Herramientas', href: '/tools' },
  { icon: BookOpen, label: 'Conocimiento', href: '/knowledge' },
  { icon: Shield, label: 'Conciencia', href: '/conciencia' },
];

export const AppLayout = ({ 
  children, 
  requireAuth = false,
  requireAdmin = false,
  showBackground = true
}: AppLayoutProps) => {
  const { user, profile, isAdmin, loading, tokensBalance, hasUnlimitedTokens } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [freeMode, setFreeMode] = useState<boolean | null>(null); // null = not loaded yet

  // Fetch free mode status
  useEffect(() => {
    const fetchFreeMode = async () => {
      const { data } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'free_mode')
        .single();
      
      if (data) {
        setFreeMode((data.value as any)?.enabled ?? false);
      } else {
        setFreeMode(false);
      }
    };
    fetchFreeMode();

    // Subscribe to changes
    const channel = supabase
      .channel('layout_free_mode')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'platform_settings',
          filter: 'key=eq.free_mode'
        },
        (payload) => {
          if (payload.new) {
            setFreeMode((payload.new as any).value?.enabled ?? false);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    if (!loading) {
      if (requireAuth && !user) {
        navigate('/auth');
      }
      if (requireAdmin && !isAdmin) {
        navigate('/');
      }
    }
  }, [user, isAdmin, loading, requireAuth, requireAdmin, navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  // Wait for both auth and freeMode to load
  if (loading || freeMode === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-primary/20 border border-primary/40 flex items-center justify-center animate-pulse">
              <Logo size="md" />
            </div>
            <div className="absolute inset-0 rounded-2xl border-2 border-primary/50 animate-ping" />
          </div>
          <p className="text-sm text-muted-foreground font-mono">Cargando...</p>
        </div>
      </div>
    );
  }

  if (requireAuth && !user) return null;
  if (requireAdmin && !isAdmin) return null;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {showBackground && <InteractiveBackground />}
      
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 max-w-7xl">
          <div className="flex h-16 items-center justify-between">
            {/* Left: Logo + Nav */}
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center gap-3 group">
                <Logo size="sm" />
                <span className="font-display font-bold text-lg">
                  LOWKEY<span className="text-primary">X</span>
                </span>
              </Link>

              {/* Desktop Nav */}
              <nav className="hidden md:flex items-center gap-1">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.href || 
                    (item.href !== '/' && location.pathname.startsWith(item.href));
                  
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300",
                        isActive 
                          ? "bg-primary/10 text-primary border border-primary/30" 
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      )}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  );
                })}
                
                {/* History Button - Only shown in /tools and when logged in */}
                {user && location.pathname.startsWith('/tools') && (
                  <Link
                    to="/history"
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300",
                      location.pathname === '/history'
                        ? "bg-primary/10 text-primary border border-primary/30" 
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    <History className="w-4 h-4" />
                    Historial
                  </Link>
                )}
              </nav>
            </div>

            {/* Right: Auth + Actions */}
            <div className="flex items-center gap-3">
              {user ? (
                <>
                  {/* Token Display - Hidden when free mode is active */}
                  {!freeMode && (
                    <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/30">
                      <Zap className="w-4 h-4 text-primary" />
                      <span className="text-sm font-mono font-bold text-primary">
                        {hasUnlimitedTokens ? '∞' : tokensBalance}
                      </span>
                    </div>
                  )}

                  {/* Admin Button */}
                  {isAdmin && (
                    <Link to="/admin">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className={cn(
                          "hidden sm:flex gap-2 border-destructive/50 text-destructive hover:bg-destructive/10",
                          location.pathname === '/admin' && "bg-destructive/10"
                        )}
                      >
                        <Shield className="w-4 h-4" />
                        Admin
                      </Button>
                    </Link>
                  )}

                  {/* User Menu - Hidden on mobile (shown in sidebar instead) */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="hidden md:flex relative w-10 h-10 rounded-full bg-primary/10 border border-primary/30 hover:bg-primary/20"
                      >
                        <User className="w-5 h-5 text-primary" />
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-terminal border-2 border-background" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72 p-0">
                      {/* User Info Header */}
                      <div className="p-4 border-b border-border bg-muted/30">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center">
                            <User className="w-6 h-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{profile?.username ? `@${profile.username}` : profile?.email?.split('@')[0] || 'Usuario'}</p>
                            <p className="text-xs text-muted-foreground truncate">{profile?.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                          <PlanBadge />
                          {/* Token display in dropdown - Hidden when free mode is active */}
                          {!freeMode && (
                            <div className="flex items-center gap-1.5 text-primary">
                              <Zap className="w-4 h-4" />
                              <span className="font-mono font-bold text-sm">
                                {hasUnlimitedTokens ? '∞' : tokensBalance}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Menu Items */}
                      <div className="p-2">
                        <DropdownMenuItem asChild>
                          <Link to="/account" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-lg">
                            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                              <Settings className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-sm">Mi Cuenta</p>
                              <p className="text-xs text-muted-foreground">Configuración y perfil</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </Link>
                        </DropdownMenuItem>
                        
                        {isAdmin && (
                          <DropdownMenuItem asChild>
                            <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-lg">
                              <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center">
                                <Shield className="w-4 h-4 text-destructive" />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-sm text-destructive">Panel Admin</p>
                                <p className="text-xs text-muted-foreground">Gestión de la plataforma</p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </Link>
                          </DropdownMenuItem>
                        )}
                      </div>
                      
                      <DropdownMenuSeparator className="my-0" />
                      
                      <div className="p-2">
                        <DropdownMenuItem 
                          onClick={handleLogout}
                          className="flex items-center gap-3 px-3 py-2.5 cursor-pointer text-destructive focus:text-destructive rounded-lg"
                        >
                          <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center">
                            <LogOut className="w-4 h-4" />
                          </div>
                          <span className="font-medium text-sm">Cerrar sesión</span>
                        </DropdownMenuItem>
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  {/* Login/Register Buttons */}
                  <Link to="/auth" className="hidden sm:block">
                    <Button variant="ghost" size="sm" className="gap-2">
                      <LogIn className="w-4 h-4" />
                      Iniciar sesión
                    </Button>
                  </Link>
                  <Link to="/auth">
                    <Button size="sm" className="gap-2 bg-primary hover:bg-primary/90 glow-primary">
                      <UserPlus className="w-4 h-4" />
                      <span className="hidden sm:inline">Registrarse</span>
                      <span className="sm:hidden">Entrar</span>
                    </Button>
                  </Link>
                </>
              )}

              {/* Mobile Menu Trigger */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden relative"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Advanced Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm md:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            
            {/* Slide-in Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 z-[101] w-[85vw] max-w-[320px] bg-gradient-to-b from-background via-background to-background/95 border-l border-primary/20 md:hidden overflow-hidden flex flex-col"
            >
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl" />
              <div className="absolute bottom-20 left-0 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />
              
              {/* Header */}
              <div className="relative p-4 border-b border-border/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/20 border border-primary/40 flex items-center justify-center">
                      <Terminal className="w-4 h-4 text-primary" />
                    </div>
                  <span className="font-display font-bold text-sm">
                    LOWKEY<span className="text-primary">X</span>
                  </span>
                  </div>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-8 h-8 rounded-lg bg-muted/50 hover:bg-muted flex items-center justify-center transition-colors"
                  >
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              </div>

              {/* User Profile Section - Only when logged in */}
              {user && (
                <Link
                  to="/account"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block m-4 p-4 rounded-xl bg-muted/30 border border-border/50 hover:border-primary/30 hover:bg-muted/50 transition-all active:scale-[0.98]"
                >
                  <div className="flex items-center gap-3">
                    <div className="relative flex-shrink-0">
                      <div className="w-11 h-11 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-terminal border-2 border-background" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {profile?.username ? (
                        <p className="font-medium text-sm text-foreground truncate">
                          @{profile.username}
                        </p>
                      ) : (
                        <p className="font-medium text-sm text-foreground truncate">
                          {profile?.email?.split('@')[0] || 'Usuario'}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground truncate">{profile?.email}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                    <PlanBadge />
                    {!freeMode && (
                      <div className="flex items-center gap-1.5 text-primary">
                        <Zap className="w-3.5 h-3.5" />
                        <span className="font-mono text-xs font-bold">
                          {hasUnlimitedTokens ? '∞' : tokensBalance}
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
              )}

              {/* Guest Section - When not logged in */}
              {!user && (
                <div className="p-4 border-b border-border/50">
                  <div className="p-3 rounded-xl bg-gradient-to-r from-amber-500/10 to-amber-500/5 border border-amber-500/20">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Modo Invitado</p>
                        <p className="text-[10px] text-muted-foreground">Acceso limitado</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Link
                        to="/auth"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex-1"
                      >
                        <Button size="sm" className="w-full bg-primary hover:bg-primary/90 text-xs h-8">
                          <UserPlus className="w-3 h-3 mr-1.5" />
                          Registrarse
                        </Button>
                      </Link>
                      <Link
                        to="/auth"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex-1"
                      >
                        <Button variant="outline" size="sm" className="w-full text-xs h-8">
                          <LogIn className="w-3 h-3 mr-1.5" />
                          Entrar
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex-1 overflow-y-auto p-4">
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-2 px-1">
                  Navegación
                </p>
                <nav className="space-y-1">
                  {navItems.map((item, index) => {
                    const isActive = location.pathname === item.href || 
                      (item.href !== '/' && location.pathname.startsWith(item.href));
                    
                    return (
                      <motion.div
                        key={item.href}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all relative overflow-hidden",
                            isActive 
                              ? "bg-primary/15 text-primary" 
                              : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                          )}
                        >
                          {isActive && (
                            <motion.div
                              layoutId="activeNav"
                              className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r-full"
                            />
                          )}
                          <div className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center",
                            isActive ? "bg-primary/20" : "bg-muted/50"
                          )}>
                            <item.icon className={cn("w-4 h-4", isActive && "text-primary")} />
                          </div>
                          <span className="font-medium text-sm">{item.label}</span>
                          {isActive && (
                            <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                          )}
                        </Link>
                      </motion.div>
                    );
                  })}
                  
                  {/* History - only when logged in */}
                  {user && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: navItems.length * 0.05 }}
                    >
                      <Link
                        to="/history"
                        onClick={() => setMobileMenuOpen(false)}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all",
                          location.pathname === '/history'
                            ? "bg-primary/15 text-primary" 
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        )}
                      >
                        <div className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center",
                          location.pathname === '/history' ? "bg-primary/20" : "bg-muted/50"
                        )}>
                          <History className="w-4 h-4" />
                        </div>
                        <span className="font-medium text-sm">Historial</span>
                      </Link>
                    </motion.div>
                  )}
                </nav>

                {/* Admin Section */}
                {isAdmin && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-2 px-1">
                      Administración
                    </p>
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-destructive hover:bg-destructive/10 transition-all"
                    >
                      <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center">
                        <Shield className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-sm">Panel Admin</span>
                      <Crown className="w-3 h-3 ml-auto text-amber-400" />
                    </Link>
                  </div>
                )}

                {/* Quick Actions */}
                {user && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-2 px-1">
                      Acciones
                    </p>
                    <Link
                      to="/account"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
                    >
                      <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center">
                        <Settings className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-sm">Configuración</span>
                    </Link>
                  </div>
                )}
              </div>

              {/* Footer - Fixed at bottom */}
              <div className="mt-auto p-4 border-t border-border/50 bg-background/80 backdrop-blur-sm">
                {user && (
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive hover:bg-destructive/20 transition-all active:scale-[0.98]"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="font-medium text-sm">Cerrar sesión</span>
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="relative z-10 flex-1">
        {children}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};
